// src/pages/MapSearch.js
// Page carte avec recherche par ville et repli intelligent sur les annonces existantes - NE PAS MODIFIER

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from '../utils/leafletConfig';
import { useTheme } from '../hooks/useTheme';
import apiClient from '../api/client';
import LocationPermissionModal from '../components/shared/LocationPermissionModal';
import { formatPriceForMap } from '../utils/formatPrice';
import { textIncludes } from '../utils/normalizeText';
import { APP_CONFIG } from '../constants/config';

const RecenterMap = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || map.getZoom(), { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
};

const FitToListings = ({ listings, userPosition }) => {
  const map = useMap();
  useEffect(() => {
    const points = listings.filter((l) => l.lat && l.lng).map((l) => [parseFloat(l.lat), parseFloat(l.lng)]);
    if (userPosition) points.push([userPosition.lat, userPosition.lng]);
    if (points.length === 0) return;
    if (points.length === 1) {
      map.flyTo(points[0], 14, { duration: 0.8 });
      return;
    }
    const bounds = L.latLngBounds(points);
    map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 14, duration: 0.8 });
  }, [listings, userPosition, map]);
  return null;
};

const MapSearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [visibleListings, setVisibleListings] = useState([]);
  const [listingsPool, setListingsPool] = useState([]); // tout le stock, pour la recherche par ville + repli
  const [defaultListings, setDefaultListings] = useState([]); // dernier résultat "normal" à retrouver si on efface la recherche
  const [selectedListing, setSelectedListing] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [userPosition, setUserPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState([APP_CONFIG.DEFAULT_LAT, APP_CONFIG.DEFAULT_LNG]);
  const [shouldFitBounds, setShouldFitBounds] = useState(false);
  const [searching, setSearching] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [showPermissionGuide, setShowPermissionGuide] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null); // { text, tone: 'warning' | 'info' }
  const [cityQuery, setCityQuery] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [nearbyResult, poolResult] = await Promise.all([
          apiClient.get(`/endpoints/listings.php?action=nearby&lat=${APP_CONFIG.DEFAULT_LAT}&lng=${APP_CONFIG.DEFAULT_LNG}&radius=30`),
          apiClient.get('/endpoints/listings.php?action=list&status=published&limit=300'),
        ]);
        const nearby = nearbyResult.listings || [];
        const pool = (poolResult.listings || []).filter((l) => l.lat && l.lng);
        setListingsPool(pool);

        // Si rien de proche par défaut, on montre directement tout le stock existant
        const initial = nearby.length > 0 ? nearby : pool;
        setVisibleListings(initial);
        setDefaultListings(initial);
      } catch (err) {
        setVisibleListings([]);
        setDefaultListings([]);
      } finally {
        setTimeout(() => setMapLoading(false), 300);
      }
    };
    loadInitial();
  }, []);

  const categoryChips = [
    { id: 'all', icon: '🔎', label: t('common.all') },
    { id: 'sejours', icon: '🏨', label: t('categories.sejours') },
    { id: 'locations', icon: '🏠', label: t('categories.locations') },
    { id: 'immobilier', icon: '🌍', label: t('categories.immobilier') },
    { id: 'services', icon: '🛎️', label: t('categories.services') },
  ];

  const filterByCategory = useCallback((list, category) => {
    if (!category || category === 'all') return list;
    return list.filter((l) => l.category === category);
  }, []);

  const fetchNearby = useCallback(async (lat, lng, category) => {
    try {
      const params = new URLSearchParams({
        action: 'nearby', lat, lng, radius: 30,
        ...(category && category !== 'all' ? { category } : {}),
      }).toString();
      const result = await apiClient.get(`/endpoints/listings.php?${params}`);
      return result.listings || [];
    } catch (err) {
      return [];
    }
  }, []);

  // Recherche par proximité, avec repli automatique sur tout le stock si rien n'est trouvé à côté
  const handleSearch = useCallback(() => {
    if (!navigator.geolocation) {
      setShowPermissionGuide(true);
      return;
    }
    setSearching(true);
    setStatusMessage(null);
    setCityQuery('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const userPos = { lat: latitude, lng: longitude };
        setUserPosition(userPos);

        const nearby = await fetchNearby(latitude, longitude, activeCategory);

        if (nearby.length > 0) {
          setVisibleListings(nearby);
          setDefaultListings(nearby);
          setStatusMessage(null);
        } else {
          // Rien à proximité → on bascule sur toutes les annonces existantes (filtrées catégorie si active)
          const fallback = filterByCategory(listingsPool, activeCategory);
          setVisibleListings(fallback);
          setDefaultListings(fallback);
          setStatusMessage(
            fallback.length > 0
              ? { text: "Aucune annonce tout près de vous — voici toutes celles disponibles", tone: 'info' }
              : { text: "Aucune annonce disponible pour le moment", tone: 'warning' }
          );
        }

        setSearching(false);
        setShouldFitBounds(true);
      },
      (error) => {
        setSearching(false);
        if (error.code === 1) setShowPermissionGuide(true);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [activeCategory, fetchNearby, filterByCategory, listingsPool]);

  const handleCategorySelect = async (categoryId) => {
    setActiveCategory(categoryId);
    setCityQuery('');
    setStatusMessage(null);

    if (userPosition) {
      const nearby = await fetchNearby(userPosition.lat, userPosition.lng, categoryId);
      if (nearby.length > 0) {
        setVisibleListings(nearby);
        setDefaultListings(nearby);
      } else {
        const fallback = filterByCategory(listingsPool, categoryId);
        setVisibleListings(fallback);
        setDefaultListings(fallback);
        setStatusMessage(
          fallback.length > 0
            ? { text: "Aucune annonce à proximité pour cette catégorie — voici tout le stock", tone: 'info' }
            : { text: "Aucune annonce dans cette catégorie", tone: 'warning' }
        );
      }
    } else {
      const filtered = filterByCategory(listingsPool, categoryId);
      setVisibleListings(filtered);
      setDefaultListings(filtered);
      if (filtered.length === 0) {
        setStatusMessage({ text: "Aucune annonce dans cette catégorie", tone: 'warning' });
      }
    }
    setShouldFitBounds(true);
  };

  // Recherche par ville/quartier - filtre en direct dans tout le stock déjà chargé
  const handleCityInput = (value) => {
    setCityQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (value.trim().length < 2) {
        setVisibleListings(defaultListings);
        setStatusMessage(null);
        return;
      }
      const base = filterByCategory(listingsPool, activeCategory);
      const matches = base.filter(
        (l) => textIncludes(l.city, value) || textIncludes(l.quartier, value)
      );
      setVisibleListings(matches);
      setShouldFitBounds(true);
      setStatusMessage(
        matches.length === 0 ? { text: `Aucune annonce trouvée pour "${value}"`, tone: 'warning' } : null
      );
    }, 350);
  };

  const clearCitySearch = () => {
    setCityQuery('');
    setVisibleListings(defaultListings);
    setStatusMessage(null);
    setShouldFitBounds(true);
  };

  const createPriceIcon = (listing) => {
    const priceLabel = formatPriceForMap(listing.price, listing.price_type || listing.priceType);
    const isSelected = selectedListing?.id === listing.id;
    return L.divIcon({
      className: 'price-marker-wrap',
      html: `<div class="fynho-marker-pop" style="
        background-color: ${isSelected ? theme.gold : theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A'};
        color: ${isSelected ? '#FFFFFF' : theme.gold};
        border: 2px solid ${theme.gold};
        border-radius: 20px;
        padding: 6px 12px;
        font-size: 13px;
        font-weight: 700;
        white-space: nowrap;
        box-shadow: 0 3px 10px rgba(0,0,0,0.25);
        font-family: -apple-system, sans-serif;
      ">${listing.quartier} · ${priceLabel}</div>`,
      iconSize: [120, 32],
      iconAnchor: [60, 16],
    });
  };

  const userIcon = L.divIcon({
    className: 'user-marker',
    html: `<div class="fynho-pulse-dot" style="width: 16px; height: 16px; border-radius: 50%; background-color: #4299E1; border: 3px solid #FFFFFF; box-shadow: 0 0 0 4px rgba(66,153,225,0.3);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

  const styles = {
    page: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg, paddingBottom: '90px' },
    header: {
      display: 'flex', alignItems: 'center', gap: '12px', padding: '16px',
      position: 'sticky', top: 0, backgroundColor: theme.bg, zIndex: 50,
      borderBottom: `1px solid ${theme.borderSoft}`,
    },
    backButton: {
      width: '38px', height: '38px', borderRadius: '50%', backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`, color: theme.textPrimary, fontSize: '18px',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'transform 150ms ease',
    },
    titleGroup: { flex: 1 },
    title: { fontSize: '18px', fontWeight: '700', color: theme.textPrimary },
    countBadge: { fontSize: '12px', color: theme.textSecondary, marginTop: '2px' },
    body: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
    searchPill: {
      display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 14px',
      backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '16px',
      transition: 'border-color 150ms ease, box-shadow 150ms ease',
    },
    searchIcon: { fontSize: '15px', opacity: 0.6, flexShrink: 0 },
    searchInput: {
      flex: 1, border: 'none', outline: 'none', backgroundColor: 'transparent',
      color: theme.textPrimary, fontSize: '14px', fontFamily: 'inherit',
    },
    clearBtn: {
      width: '20px', height: '20px', borderRadius: '50%', backgroundColor: theme.bgInput,
      border: 'none', color: theme.textSecondary, fontSize: '11px', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    chipsRow: { display: 'flex', gap: '8px', overflowX: 'auto', scrollbarWidth: 'none' },
    chip: {
      display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 15px', borderRadius: '20px',
      backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.textSecondary,
      fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
      transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    chipActive: { backgroundColor: theme.gold, color: '#FFFFFF', border: `1px solid ${theme.gold}`, transform: 'scale(1.03)', boxShadow: `0 3px 10px ${theme.goldBorder}` },
    searchButton: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '15px',
      background: `linear-gradient(135deg, ${theme.gold}, ${theme.mode === 'light' ? '#059669' : '#16A34A'})`,
      color: '#FFFFFF', border: 'none', borderRadius: '14px', fontSize: '14px', fontWeight: '700',
      cursor: 'pointer', boxShadow: `0 6px 16px ${theme.goldBorder}`,
      transition: 'transform 150ms ease',
    },
    statusBanner: (tone) => ({
      padding: '10px 14px', borderRadius: '10px', fontSize: '12px', textAlign: 'center',
      backgroundColor: tone === 'warning' ? 'rgba(237, 137, 54, 0.12)' : 'rgba(66, 153, 225, 0.12)',
      border: `1px solid ${tone === 'warning' ? 'rgba(237, 137, 54, 0.35)' : 'rgba(66, 153, 225, 0.35)'}`,
      color: tone === 'warning' ? '#DD6B20' : '#4299E1',
    }),
    mapCard: {
      position: 'relative', width: '100%', height: 'clamp(340px, 55vh, 520px)', borderRadius: '22px',
      overflow: 'hidden', boxShadow: theme.mode === 'light' ? '0 8px 28px rgba(0,0,0,0.12)' : '0 8px 28px rgba(0,0,0,0.5)',
      border: `1px solid ${theme.border}`, animation: 'mapCardIn 500ms cubic-bezier(0.16, 1, 0.3, 1)',
    },
    mapSkeleton: { position: 'absolute', inset: 0, backgroundColor: theme.bgCard, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 },
    skeletonPulse: { width: '48px', height: '48px', borderRadius: '50%', backgroundColor: theme.goldSoft, animation: 'skeletonPulse 1.2s ease-in-out infinite' },
    selectedCard: {
      display: 'flex', gap: '12px', padding: '14px', backgroundColor: theme.bgCard,
      borderRadius: '16px', border: `1px solid ${theme.goldBorder}`, cursor: 'pointer',
      boxShadow: theme.shadow, position: 'relative', animation: 'slideUpCard 300ms cubic-bezier(0.16, 1, 0.3, 1)',
    },
    cardImage: { width: '76px', height: '76px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 },
    cardContent: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px', minWidth: 0 },
    cardTitle: { fontSize: '14px', fontWeight: '600', color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    cardLocation: { fontSize: '12px', color: theme.textSecondary },
    cardPrice: { fontSize: '14px', fontWeight: '700', color: theme.gold },
    closeCardBtn: {
      position: 'absolute', top: '8px', right: '8px', width: '24px', height: '24px', borderRadius: '50%',
      backgroundColor: theme.bgInput, border: `1px solid ${theme.border}`, color: theme.textSecondary,
      fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
  };

  return (
    <div style={styles.page}>
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        @keyframes mapCardIn { from { opacity: 0; transform: translateY(16px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes slideUpCard { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes skeletonPulse { 0%, 100% { opacity: 0.5; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.05); } }
        @keyframes fynhoMarkerPop { from { opacity: 0; transform: scale(0.5); } to { opacity: 1; transform: scale(1); } }
        .fynho-marker-pop { animation: fynhoMarkerPop 280ms cubic-bezier(0.34, 1.56, 0.64, 1); }
        .fynho-pulse-dot { animation: fynhoMarkerPop 280ms ease-out; }
        .leaflet-container { font-family: -apple-system, sans-serif; }
      `}</style>

      <Helmet>
        <title>Carte des annonces | Fynho</title>
        <meta name="description" content="Explorez les annonces immobilières à proximité sur la carte au Cameroun." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.titleGroup}>
          <div style={styles.title}>🗺️ {t('home.viewMap')}</div>
          <div style={styles.countBadge}>
            {visibleListings.length} {visibleListings.length > 1 ? t('home.listings') : t('home.listing')}
          </div>
        </div>
      </div>

      <div style={styles.body}>
        {/* Recherche par ville/quartier */}
        <div style={styles.searchPill}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="Rechercher une ville ou un quartier..."
            value={cityQuery}
            onChange={(e) => handleCityInput(e.target.value)}
          />
          {cityQuery && (
            <button style={styles.clearBtn} onClick={clearCitySearch}>✕</button>
          )}
        </div>

        <div style={styles.chipsRow} className="hide-scrollbar">
          {categoryChips.map((chip) => (
            <div
              key={chip.id}
              style={{ ...styles.chip, ...(activeCategory === chip.id ? styles.chipActive : {}) }}
              onClick={() => handleCategorySelect(chip.id)}
            >
              <span>{chip.icon}</span>
              {chip.label}
            </div>
          ))}
        </div>

        <button
          style={styles.searchButton}
          onClick={handleSearch}
          disabled={searching}
          onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
          onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          🧭 {searching ? t('common.loading') : t('home.useLocation')}
        </button>

        {statusMessage && (
          <div style={styles.statusBanner(statusMessage.tone)}>{statusMessage.text}</div>
        )}

        <div style={styles.mapCard}>
          {mapLoading && (
            <div style={styles.mapSkeleton}><div style={styles.skeletonPulse} /></div>
          )}
          <MapContainer center={mapCenter} zoom={APP_CONFIG.DEFAULT_MAP_ZOOM} style={{ width: '100%', height: '100%' }}>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            {!shouldFitBounds && <RecenterMap center={mapCenter} zoom={14} />}
            {shouldFitBounds && <FitToListings listings={visibleListings} userPosition={userPosition} />}

            {userPosition && (
              <Marker position={[userPosition.lat, userPosition.lng]} icon={userIcon}>
                <Popup>Vous êtes ici</Popup>
              </Marker>
            )}

            {visibleListings.map((listing) => (
              listing.lat && listing.lng ? (
                <Marker
                  key={listing.id}
                  position={[parseFloat(listing.lat), parseFloat(listing.lng)]}
                  icon={createPriceIcon(listing)}
                  eventHandlers={{ click: () => setSelectedListing(listing) }}
                >
                  <Popup>
                    <strong>{listing.title}</strong>
                    <br />
                    {listing.quartier} — {formatPriceForMap(listing.price, listing.price_type || listing.priceType)}
                    {listing.distanceKm != null && (<><br />{listing.distanceKm} km</>)}
                  </Popup>
                </Marker>
              ) : null
            ))}
          </MapContainer>
        </div>

        {selectedListing && (
          <div style={styles.selectedCard} onClick={() => navigate(`/listing/${selectedListing.slug}`)}>
            <button
              style={styles.closeCardBtn}
              onClick={(e) => { e.stopPropagation(); setSelectedListing(null); }}
            >
              ✕
            </button>
            <img src={selectedListing.images?.[0] || '/images/placeholder.png'} alt={selectedListing.title} style={styles.cardImage} />
            <div style={styles.cardContent}>
              <div style={styles.cardTitle}>{selectedListing.title}</div>
              <div style={styles.cardLocation}>
                📍 {selectedListing.quartier}, {selectedListing.city}
                {selectedListing.distanceKm != null && ` · ${selectedListing.distanceKm} km`}
              </div>
              <div style={styles.cardPrice}>
                {formatPriceForMap(selectedListing.price, selectedListing.price_type || selectedListing.priceType)}
              </div>
            </div>
          </div>
        )}
      </div>

      <LocationPermissionModal open={showPermissionGuide} onClose={() => setShowPermissionGuide(false)} />
    </div>
  );
};

export default MapSearch;